/* eslint-disable react/jsx-wrap-multilines */
import { memo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import InputAdornment from '@material-ui/core/InputAdornment';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import TextField from '@base/TextField/TextField';
import Button from '@base/Button/Button';
import CountryMenu from './CountryMenu';
import { Spacings, Fonts, TextColors } from '../../../styleConstants';
import Constants from './constants';

const FlexDiv = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_8B};
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      margin-bottom: 16px;
  };`}
`;

const InputField = styled(TextField)`
  &.MuiOutlinedInput-root {
    .MuiOutlinedInput-input {
      ${({ theme }) => `${theme.breakpoints.down('mobile')} {
        font-size: 12px;
      };`}
      color: ${({ theme }) => theme.colors.fontGreyV2};
    }
  }
`;

const StyledP1 = styled(P1)`
  margin-right: ${Spacings.SPACING_1B};
`;

const StyledH6 = styled(H6)`
  font-weight: ${Fonts.BOLD};
`;

const Header = styled(H2)`
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      font-size: 16px;
  };`}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacings.SPACING_8B};
  ${({ theme }) => `${theme.breakpoints.down('mobile')} {
      margin-top: 16px;
  };`}
`;

const EditDiv = styled.div`
  position: relative;
`;

const EnterNumberStep = ({
  accountType,
  changeAccountType,
  changeCountry,
  country,
  countries,
  mobileNumber,
  setMobileNumber,
  submitNumber,
  switchLoginType,
  errorMsg,
  sourceType,
  isTTU
}) => {
  const onNumberChange = (e) => {
    const phone = e.target.value.replace(/ /g, '');
    if (phone.match(/^\d+$/) || phone === '') {
      if (country?.code === 'IN' && phone.length > 10) {
        // do nothing
      } else {
        setMobileNumber(phone);
      }
    }
  };
  const validMobileNumber = () => {
    if (
      mobileNumber.match(/^\d+$/) &&
      country?.code === 'IN' &&
      mobileNumber.length !== 10
    ) {
      return false;
    }
    if (mobileNumber === '') {
      return false;
    }
    return true;
  };
  const toggleAccountType = (e) => {
    e.preventDefault();
    changeAccountType();
  };
  const switchToEmailLogin = (e) => {
    e.preventDefault();
    switchLoginType();
  };
  const onButtonClick = () => {
    if (validMobileNumber()) submitNumber();
  };
  const isLoginAccountType = accountType === Constants.ACCOUNT_TYPE_LOGIN;
  return (
    <>
      {!(sourceType === 'marketing') && !isTTU && (
        <>
          <Header>{isLoginAccountType ? 'Login' : 'Join Unacademy'}</Header>
          <FlexDiv>
            <StyledP1>or</StyledP1>
            <UnderlineLink
              color={TextColors.GREEN}
              onClick={toggleAccountType}
              href="#"
            >
              <StyledH6 color={TextColors.GREEN}>
                {isLoginAccountType
                  ? 'create your account'
                  : 'login to your account'}
              </StyledH6>
            </UnderlineLink>
          </FlexDiv>
        </>
      )}
      <EditDiv>
        <InputField
          fullWidth
          startAdornment={
            <InputAdornment>
              <CountryMenu
                changeCountry={changeCountry}
                country={country}
                countries={countries}
              />
            </InputAdornment>
          }
          placeholder="Enter your mobile number"
          onChange={onNumberChange}
          value={mobileNumber}
          inputProps={{
            type: 'tel',
            maxLength: 15,
            minLength: 3,
            required: true
          }}
          type="tel"
          autoFocus={!isTTU}
          error={errorMsg !== ''}
          helperText={errorMsg}
          enterCallback={onButtonClick}
        />
      </EditDiv>
      <ButtonWrapper>
        <Button
          variant={validMobileNumber() ? 'filled' : 'disabled'}
          label={isLoginAccountType && !isTTU ? 'Login' : 'Continue'}
          onClick={onButtonClick}
          form="enter_number"
          grid={3}
        />
        {isLoginAccountType && !(sourceType === 'marketing') && !isTTU ? (
          <UnderlineLink onClick={switchToEmailLogin} href="#">
            <StyledH6>Continue with email</StyledH6>
          </UnderlineLink>
        ) : null}
      </ButtonWrapper>
    </>
  );
};

export default memo(EnterNumberStep);

EnterNumberStep.propTypes = {
  accountType: PropTypes.oneOf([
    Constants.ACCOUNT_TYPE_LOGIN,
    Constants.ACCOUNT_TYPE_SIGNUP
  ]),
  mobileNumber: PropTypes.string.isRequired,
  country: PropTypes.objectOf(PropTypes.any).isRequired,
  countries: PropTypes.arrayOf(PropTypes.any).isRequired,
  changeCountry: PropTypes.func.isRequired,
  changeAccountType: PropTypes.func.isRequired,
  setMobileNumber: PropTypes.func.isRequired,
  submitNumber: PropTypes.func.isRequired,
  switchLoginType: PropTypes.func.isRequired,
  errorMsg: PropTypes.string.isRequired,
  sourceType: PropTypes.string,
  isTTU: PropTypes.bool
};

EnterNumberStep.defaultProps = {
  accountType: Constants.ACCOUNT_TYPE_LOGIN,
  sourceType: '',
  isTTU: false
};
